import { useDashboardStore } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';

function formatCountdown(target: string | null | undefined): string {
  if (!target) return '—';
  const now = Date.now();
  const ms = new Date(target).getTime() - now;
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'now';
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const MARKET_LABEL: Record<string, string> = {
  open: 'open',
  closed: 'closed',
  holiday: 'holiday',
};

const MARKET_COLOR: Record<string, string> = {
  open: '#2ea043',
  closed: '#8b949e',
  holiday: '#d29922',
};

/**
 * Compact calendar HUD.  On desktop, four labelled pills; on tablet,
 * three (drops the friday party countdown which is the lowest-signal);
 * on mobile, only the market status + nearest boundary countdown.
 */
export function CalendarHud() {
  const cal = useDashboardStore((s) => s.building?.calendar);
  const bp = useBreakpoint();

  if (!cal) {
    return (
      <div style={{ color: '#8b949e', fontSize: 12 }}>calendar loading…</div>
    );
  }

  const marketColor = MARKET_COLOR[cal.market_state] ?? '#e6edf3';
  const marketLabel = MARKET_LABEL[cal.market_state] ?? cal.market_state;

  // Nearest boundary the operator cares about right now: open if closed,
  // close if open.  Keeps the mobile HUD pithy.
  const nearestLabel = cal.market_state === 'open' ? 'closes in' : 'opens in';
  const nearestTarget =
    cal.market_state === 'open' ? cal.next_daily_close_at : cal.next_session_open_at;

  // Native ``title=`` tooltips on each pill -- short, factual, ≤120 chars
  // so the browser doesn't word-wrap awkwardly.  Russian to match the
  // dashboard chrome.
  const marketTitle =
    cal.market_state === 'open'
      ? 'Биржа MOEX открыта. Поды могут торговать. Закрытие сегодня в 18:45 MSK (15:45 UTC).'
      : cal.market_state === 'holiday'
        ? 'Биржевой выходной — торгов нет. Поды простаивают и крутят ambient-loop.'
        : 'Биржа MOEX закрыта. Поды стоят. Откроется в 10:00 MSK (07:00 UTC) ближайшего торгового дня.';
  const nextOpenTitle =
    'Время до открытия следующей торговой сессии MOEX (07:00 UTC). Считается с учётом праздничного календаря.';
  const dailyCloseTitle =
    'Время до daily-close джоба (21:00 UTC): фиксация EOD NAV, перерасчёт капитала между подами, leaderboard.';
  const fridayPartyTitle =
    'До еженедельного celebration-ивента в пятницу 15:50 UTC: подводим итоги недели по подам и моделям.';

  if (bp === 'mobile') {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 12,
          color: '#8b949e',
        }}
      >
        <span title={marketTitle}>
          market:{' '}
          <strong style={{ color: marketColor }}>{marketLabel}</strong>
        </span>
        <span
          title={
            cal.market_state === 'open' ? dailyCloseTitle : nextOpenTitle
          }
        >
          {nearestLabel}{' '}
          <strong style={{ color: '#e6edf3' }}>{formatCountdown(nearestTarget)}</strong>
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: bp === 'tablet' ? 12 : 18,
        fontSize: 12,
        color: '#8b949e',
        flexWrap: 'wrap',
      }}
    >
      <span title={marketTitle}>
        market:{' '}
        <strong style={{ color: marketColor }}>{marketLabel}</strong>
      </span>
      <span title={nextOpenTitle}>
        next open in{' '}
        <strong style={{ color: '#e6edf3' }}>{formatCountdown(cal.next_session_open_at)}</strong>
      </span>
      <span title={dailyCloseTitle}>
        daily close in{' '}
        <strong style={{ color: '#e6edf3' }}>{formatCountdown(cal.next_daily_close_at)}</strong>
      </span>
      {bp === 'desktop' ? (
        <span title={fridayPartyTitle}>
          friday party in{' '}
          <strong style={{ color: '#e6edf3' }}>
            {formatCountdown(cal.next_friday_celebration_at)}
          </strong>
        </span>
      ) : null}
    </div>
  );
}
